import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const VISIBLE_COUNT = 6;

const photos = [
  {
    src: '/assets/img/76f1f6924aa1.webp',
    alt: 'Birthday party under decorated stretch tent',
    title: 'Milestone Birthday Bash',
  },
  {
    src: '/assets/img/84d9c4232731.webp',
    alt: 'Garden party under stretch tent',
    title: 'Summer Garden Party',
  },
  {
    src: '/assets/img/1c6aed509a62.webp',
    alt: 'Cocktail party under stretch tent at dusk',
    title: 'Rooftop Cocktail Evening',
  },
  {
    src: '/assets/img/8c08ca7fbc83.webp',
    alt: 'Graduation celebration under stretch tent',
    title: 'Graduation Celebration',
  },
  {
    src: '/assets/img/a874f2e12a5b.webp',
    alt: 'Bespoke floral ceiling installation under stretch tent',
    title: 'Cascading Floral Ceiling',
  },
  {
    src: '/assets/img/a391e6deefcd.webp',
    alt: 'Draped fabric and chandelier bespoke tent decor',
    title: 'Ivory & Gold Drape Setup',
  },
  {
    src: '/assets/img/b8ceced210d1.webp',
    alt: 'Tropical jungle themed bespoke tent decor',
    title: 'Tropical Jungle Theme',
  },
  {
    src: '/assets/img/875051dbe36d.webp',
    alt: 'Bohemian macrame bespoke tent decor',
    title: 'Boho Macrame Styling',
  },
  {
    src: '/assets/img/1524ce5526c1.webp',
    alt: 'Modern minimalist bespoke tent decor',
    title: 'Modern Minimalist Styling',
  },
  {
    src: '/assets/img/87c38d215734.webp',
    alt: 'Enchanted forest themed bespoke tent decor',
    title: 'Enchanted Forest Theme',
  },
  {
    src: '/assets/img/9aff94783e59.webp',
    alt: 'Art deco gatsby themed bespoke tent decor',
    title: 'Art Deco Gatsby Night',
  },
  {
    src: '/assets/img/3d6a1c9f2f89.webp',
    alt: 'Rustic barn style bespoke tent decor',
    title: 'Rustic Barn Styling',
  },
  {
    src: '/assets/img/b8e72fcab005.webp',
    alt: 'Mediterranean themed bespoke tent decor',
    title: 'Mediterranean Summer Feast',
  },
];

export default function InspirationGallery() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevPhoto = () =>
    setLightboxIndex((prev) =>
      prev !== null ? (prev - 1 + photos.length) % photos.length : 0
    );
  const nextPhoto = () =>
    setLightboxIndex((prev) =>
      prev !== null ? (prev + 1) % photos.length : 0
    );

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevPhoto();
      if (e.key === 'ArrowRight') nextPhoto();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [lightboxIndex]);

  return (
    <section className="py-20 bg-gray-50 border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-3">Our Bespoke Decor Gallery</h2>
          <p className="text-gray-500 text-base">
            A glimpse into the world we create. Click any photo to explore.
          </p>
        </div>

        {/* 3-column grid — 2 rows only */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {photos.slice(0, VISIBLE_COUNT).map((photo, index) => (
            <div
              key={index}
              onClick={() => openLightbox(index)}
              className="group relative h-64 rounded-2xl overflow-hidden bg-gray-100 cursor-pointer"
            >
              <img
                src={photo.src}
                alt={photo.alt}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-end justify-start p-4">
                <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0 transition-transform">
                  {photo.title}
                </span>
              </div>
              <div className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-white/20 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <i className="ri-zoom-in-line text-base"></i>
              </div>
            </div>
          ))}
        </div>

        {/* See more */}
        <div className="flex justify-center mt-10">
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-gray-300 text-gray-700 text-sm font-medium hover:border-gray-900 hover:text-gray-900 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            See the full gallery
            <i className="ri-arrow-right-line text-base"></i>
          </Link>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div
            className="relative max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 w-10 h-10 bg-white/20 hover:bg-white/30 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Image */}
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src={photos[lightboxIndex].src}
                alt={photos[lightboxIndex].alt}
                className="w-full max-h-[75vh] object-cover object-top"
              />

              {/* Title overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-6 py-4">
                <p className="text-white font-medium text-lg">{photos[lightboxIndex].title}</p>
                <p className="text-white/70 text-sm mt-0.5">{lightboxIndex + 1} / {photos.length}</p>
              </div>

              {/* Prev */}
              <button
                onClick={prevPhoto}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/80 hover:bg-white text-gray-800 rounded-full flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>

              {/* Next */}
              <button
                onClick={nextPhoto}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/80 hover:bg-white text-gray-800 rounded-full flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </div>

            {/* Counter dots */}
            <div className="flex justify-center gap-1.5 mt-4 flex-wrap">
              {photos.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                    i === lightboxIndex ? 'bg-teal-400 w-6' : 'bg-white/40 w-2'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
